<template>
	<div class="phone-input" :class="{ 'phone-input--error': !!errorMessage }">
		<input
			ref="input"
			type="tel"
			:name="name"
			:required="required"
			:disabled="disabled"
			autocomplete="tel"
			class="input"
			@blur="onBlur" />
		<p v-if="errorMessage" class="help is-danger">
{{ errorMessage }}
</p>
	</div>
</template>

<script>
	// intl-tel-input v23 — EAE'nin iletisim formundaki bileşenin karşılığı.
	// WithUtils paketi kullanıldığı için CDN'den ayrıca utils.js çekilmiyor.
	import intlTelInput from 'intl-tel-input/build/js/intlTelInputWithUtils.js';
	import 'intl-tel-input/build/css/intlTelInput.css';
	import trTranslations from 'intl-tel-input/build/js/i18n/tr/index.js';
	import enTranslations from 'intl-tel-input/build/js/i18n/en/index.js';

	// The CRM identifies a country by a SAP code, which is its ISO 3166-1 alpha-2 code for
	// 232 of the 253 countries it returns. These four are real countries where it is not.
	// The rest of the mismatches have no dial code at all - Antarctica, NATO, the European
	// Union - so there is nothing to map them to.
	const SAP_TO_ISO2 = {
		tp: 'tl', // Doğu Timor
		ko: 'xk', // Kosova
		mne: 'me', // Montenegro
		srb: 'rs' // Sırbistan
	};

	// TR ve EN sayfaların birbirinden ayrıldığı tek yer burası.
	const LOCALE_CONFIG = {
		tr: {
			initialCountry: 'tr',
			onlyCountries: ['tr'],
			maxlength: null,
			i18n: trTranslations,
			invalidMessage: 'Geçerli bir telefon numarası giriniz.',
			requiredMessage: 'Bu alan zorunludur.'
		},
		en: {
			// No country until the form has one. The field used to open on the United
			// States, flag, +1 and a 201-555 placeholder, which reads as an answer the
			// visitor never gave - and on a form whose first question is the country.
			initialCountry: '',
			// Türkiye used to be excluded here to mirror EAE's own English contact form. Our
			// form is not the same shape: its country dropdown lists every country in both
			// languages, Türkiye included, and the dial code is expected to follow that
			// choice. Excluding it made the one country a visitor could pick but not dial.
			maxlength: 15,
			i18n: enTranslations,
			invalidMessage: 'Please enter a valid phone number.',
			requiredMessage: 'This field is required.'
		}
	};

	export default {
		name: 'PhoneInput',
		props: {
			// E.164 formatında numara, ör. +905012345678
			value: {
				type: String,
				default: ''
			},
			// 'tr' | 'en'. Verilmezse localStorage'daki lang'den türetilir.
			locale: {
				type: String,
				default: null
			},
			name: {
				type: String,
				default: 'phone_number'
			},
			required: {
				type: Boolean,
				default: false
			},
			disabled: {
				type: Boolean,
				default: false
			},
			// EAE'deki davranış: numara geçerli hale gelince fazladan rakam girilemiyor.
			stopAtValid: {
				type: Boolean,
				default: true
			}
		},
		data() {
			return {
				iti: null,
				errorMessage: '',
				lastEmitted: ''
			};
		},
		computed: {
			resolvedLocale() {
				if (this.locale) {
					return LOCALE_CONFIG[this.locale] ? this.locale : 'en';
				}
				const lang = window.localStorage.getItem('lang') || 'tr-TR';
				return lang.toLowerCase().startsWith('tr') ? 'tr' : 'en';
			},
			config() {
				return LOCALE_CONFIG[this.resolvedLocale];
			}
		},
		watch: {
			resolvedLocale() {
				this.destroyIti();
				this.$nextTick(this.initIti);
			},
			value(next) {
				if (this.iti && next !== this.lastEmitted) {
					this.iti.setNumber(next || '');
				}
			}
		},
		mounted() {
			this.initIti();
		},
		beforeDestroy() {
			this.destroyIti();
		},
		methods: {
			initIti() {
				const input = this.$refs.input;
				const config = this.config;

				if (config.maxlength) {
					input.setAttribute('maxlength', String(config.maxlength));
				} else {
					input.removeAttribute('maxlength');
				}

				const options = {
					separateDialCode: true,
					autoPlaceholder: 'aggressive',
					initialCountry: config.initialCountry,
					i18n: config.i18n
				};
				if (config.onlyCountries) options.onlyCountries = config.onlyCountries;
				if (config.excludeCountries) options.excludeCountries = config.excludeCountries;

				this.iti = intlTelInput(input, options);

				// Bayrak + kod kutusu ile input'un tek satırda ve tam genişlikte durması için.
				if (input.parentNode.classList.contains('iti')) {
					input.parentNode.style.width = '100%';
					input.parentNode.style.display = 'flex';
				}

				if (this.value) {
					this.iti.setNumber(this.value);
				}

				input.addEventListener('keydown', this.onKeydown);
				input.addEventListener('input', this.onInput);
				input.addEventListener('countrychange', this.onCountryChange);
			},
			destroyIti() {
				const input = this.$refs.input;
				if (input) {
					input.removeEventListener('keydown', this.onKeydown);
					input.removeEventListener('input', this.onInput);
					input.removeEventListener('countrychange', this.onCountryChange);
				}
				if (this.iti) {
					this.iti.destroy();
					this.iti = null;
				}
			},
			onKeydown(e) {
				if (!this.stopAtValid || !this.iti) return;
				if (!/^\d$/.test(e.key)) return;
				// Seçili aralığın üzerine yazılıyorsa engelleme.
				const el = e.target;
				if (el.selectionStart !== el.selectionEnd) return;
				try {
					if (this.iti.isValidNumber()) e.preventDefault();
				} catch (err) { /* utils henüz hazır değilse yoksay */ }
			},
			onInput() {
				const el = this.$refs.input;
				el.value = this.applyPlaceholderMask(el.value, el.getAttribute('placeholder') || '');

				const hasDigits = el.value.replace(/\D/g, '').length > 0;
				if (hasDigits && !this.isValid()) {
					this.errorMessage = this.config.invalidMessage;
				} else {
					this.errorMessage = '';
				}

				this.emitValue();
			},
			onCountryChange() {
				this.onInput();
			},
			// Placeholder'daki rakam olmayan karakterleri (boşluk, tire, parantez)
			// girilen rakamların arasına serpiştirerek canlı biçimlendirme yapar.
			applyPlaceholderMask(raw, placeholder) {
				const digits = raw.replace(/\D/g, '');
				if (!placeholder || !digits) return digits;

				let out = '';
				let di = 0;
				for (let i = 0; i < placeholder.length && di < digits.length; i++) {
					if (/\d/.test(placeholder[i])) {
						out += digits[di++];
					} else if (di > 0) {
						out += placeholder[i];
					}
				}
				while (di < digits.length) out += digits[di++];
				return out;
			},
			isValid() {
				if (!this.iti) return false;
				try {
					return this.iti.isValidNumber();
				} catch (err) {
					return false;
				}
			},
			emitValue() {
				const number = this.iti ? this.iti.getNumber() : '';
				this.lastEmitted = number;
				this.$emit('input', number);
				this.$emit('valid', this.isValid());
			},
			onBlur() {
				this.$emit('blur');
			},
			// Formun submit anında çağırması için: EAE'deki submit validasyonunun karşılığı.
			validate() {
				const el = this.$refs.input;
				const hasDigits = el && el.value.replace(/\D/g, '').length > 0;

				if (!hasDigits) {
					this.errorMessage = this.required ? this.config.requiredMessage : '';
					return !this.required;
				}
				if (!this.isValid()) {
					this.errorMessage = this.config.invalidMessage;
					return false;
				}
				this.errorMessage = '';
				this.emitValue();
				return true;
			},
			/**
			 * Shows a message the component did not produce itself. The CRM validates the
			 * number more strictly than libphonenumber does - it wants a real Turkish
			 * operator prefix - so its refusal has to land under this field rather than in
			 * a toast that does not say which box is wrong.
			 */
			showError(message) {
				this.errorMessage = message || '';
			},
			getSelectedCountry() {
				return this.iti ? this.iti.getSelectedCountryData() : null;
			},
			/**
			 * Points the flag and dial code at a country the form has selected elsewhere.
			 * Takes the CRM's SAP code. Any digits already typed are kept and re-masked
			 * against the new country's placeholder.
			 *
			 * Returns false when the code names something intl-tel-input has no dial code
			 * for - Antarctica, NATO, and the rest of the CRM's non-countries - in which
			 * case the field is left exactly as it was rather than cleared.
			 */
			setCountry(sapCode) {
				if (!this.iti || !sapCode) return false;

				const code = SAP_TO_ISO2[sapCode.toLowerCase()] || sapCode.toLowerCase();
				try {
					this.iti.setCountry(code);
				} catch (err) {
					return false;
				}

				// setCountry ignores a code it does not recognise instead of reporting it,
				// so read the selection back rather than trusting the call.
				const selected = this.iti.getSelectedCountryData();
				if (!selected || selected.iso2 !== code) return false;

				this.onInput();
				return true;
			}
		}
	};
</script>

<style lang="scss">
	.phone-input {
		.iti {
			width: 100%;
			display: flex;
		}

		.iti input.input {
			flex: 1;
			min-width: 0;
		}
	}

	.phone-input--error input.input {
		border-color: #f14668;
	}
</style>
